import type { ModalActions } from "@/hooks/useModal";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import type { DealStage } from "@/contexts/DealStagesContext";
import { db } from "@/lib/supabase";
import { diffRecords, recordAudit } from "@/lib/audit";
import { useState } from "react";

interface ConfirmDeleteProjectModalProps extends ModalActions {
    project: ProjectSummary;
    /**
     * Configured deal stages, passed in (not read from context) because the modal
     * renders in a separate React root via useModal and can't see app providers.
     */
    dealStages: DealStage[];
    onConfirm: () => void;
}

export function ConfirmDeleteProjectModal({
    project,
    dealStages,
    onConfirm,
    close,
}: ConfirmDeleteProjectModalProps) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { markSelfEdit } = useRecentlyChangedRowsApi();
    const lostStage = dealStages.find((s) => s.kind === "lost");

    const handleConfirm = async () => {
        setError(null);
        setSubmitting(true);
        try {
            if (!lostStage) {
                setError("Could not find the reserved 'Lost' deal stage");
                setSubmitting(false);
                return;
            }
            markSelfEdit("projects", project.id);
            const { error: updateError } = await db.projects.update(project.id, {
                deal_stage_id: lostStage.id,
                likelihood_to_close: lostStage.likelihood_to_close,
            });
            if (updateError) {
                setError("Failed to mark project as lost");
                setSubmitting(false);
                return;
            }
            recordAudit({
                table: "projects",
                recordId: project.id,
                operation: "UPDATE",
                changes: diffRecords(
                    { deal_stage: project.deal_stage, likelihood_to_close: project.likelihood_to_close },
                    { deal_stage: "Lost", likelihood_to_close: 0 },
                ),
                recordLabel: project.name,
                officeId: project.office_id ?? null,
            });
            onConfirm();
            close();
        } catch (err) {
            setError("Failed to mark project as lost");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            {error && (
                <div className="error-banner">
                    <span className="error-banner__message">{error}</span>
                </div>
            )}
            <p>
                Are you sure you want to mark "{project.name}" as lost? This action will hide the project from all views.
            </p>
            <div className="modal-actions">
                <button
                    type="button"
                    className="secondary-button"
                    onClick={close}
                    disabled={submitting}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="danger-button"
                    onClick={handleConfirm}
                    disabled={submitting}
                >
                    {submitting ? "Marking as Lost..." : "Mark as Lost"}
                </button>
            </div>
        </div>
    );
}

